// src/pages/Profile.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  apiGetProfile, apiUpdateProfile, apiGetCredits,
  apiChangePassword, apiLookupPincode,
  apiRequestEmailChange, apiConfirmEmailChange,
} from '../services/api';
import OtpInput from '../components/OtpInput';
import styles from './Profile.module.css';

// ── Small reusable password field with show/hide eye toggle ────────────────
function PasswordField({ label, value, onChange, placeholder }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>{label}</label>
      <div className={styles.passwordWrap}>
        <input
          className={styles.input}
          type={visible ? 'text' : 'password'}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          autoComplete="new-password"
        />
        <button
          type="button"
          className={styles.eyeBtn}
          onClick={() => setVisible(v => !v)}
          tabIndex={-1}
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? (
            <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
              <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.4"/>
              <circle cx="10" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.4"/>
            </svg>
          ) : (
            <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
              <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.4"/>
              <circle cx="10" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.4"/>
              <line x1="3" y1="17" x2="17" y2="3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Inline Email Change ─────────────────────────────────────────────────────
// Three states: display (read-only + "Change" link), edit (new email entry),
// otp (code entry, verifies against the NEW email address).
function EmailChangeField({ currentEmail, onChanged }) {
  const [state, setState]   = useState('display'); // display | edit | otp
  const [newEmail, setNewEmail] = useState('');
  const [otp, setOtp]           = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const startEdit = () => {
    setNewEmail('');
    setError('');
    setState('edit');
  };

  const cancelEdit = () => {
    setError('');
    setState('display');
  };

  const handleSendCode = async () => {
    if (!/\S+@\S+\.\S+/.test(newEmail)) { setError('Please enter a valid email address.'); return; }
    setLoading(true); setError('');
    try {
      await apiRequestEmailChange(newEmail);
      setOtp('');
      setResendCooldown(60);
      setState('otp');
    } catch (err) {
      setError(err.message || 'Could not send verification code.');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      await apiRequestEmailChange(newEmail);
      setResendCooldown(60);
      setOtp('');
    } catch (err) {
      setError(err.message || 'Could not resend code.');
      if (err.waitSeconds) setResendCooldown(err.waitSeconds);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) { setError('Please enter the 6-digit code.'); return; }
    setLoading(true); setError('');
    try {
      await apiConfirmEmailChange(otp);
      setState('display');
      onChanged(newEmail);
    } catch (err) {
      let msg = err.message || 'Verification failed.';
      if (!err.locked && typeof err.attemptsLeft === 'number') msg = `${msg} (${err.attemptsLeft} attempt${err.attemptsLeft === 1 ? '' : 's'} left)`;
      setError(msg);
      setOtp('');
    } finally { setLoading(false); }
  };

  // state === 'otp'

  if (state === 'display') {
    return (
      <div className={styles.field}>
        <label className={styles.fieldLabel}>Email</label>
        <div className={styles.passwordWrap}>
          <input className={styles.input} value={currentEmail} disabled readOnly />
        </div>
        <button type="button" className={styles.deleteLink} style={{ color: '#1A1A1A', alignSelf: 'flex-start', marginTop: '0.2rem' }} onClick={startEdit}>
          Change email address
        </button>
      </div>
    );
  }

  if (state === 'edit') {
    return (
      <div className={styles.field}>
        <label className={styles.fieldLabel}>New Email Address</label>
        <input className={styles.input} type="email" placeholder="Enter your new email address"
          value={newEmail} onChange={e => setNewEmail(e.target.value)} />
        {error && <div className={styles.errorBanner} style={{ marginTop: '0.5rem' }}>{error}</div>}
        <div className={styles.detailsActions} style={{ marginTop: '0.6rem' }}>
          <button type="button" className={styles.saveBtn} disabled={loading} onClick={handleSendCode}>
            {loading ? <span className={styles.spinnerSm} /> : null}
            Send Verification Code
          </button>
          <button type="button" className={styles.actionBtn} onClick={cancelEdit}>Cancel</button>
        </div>
      </div>
    );
  }

  // state === 'otp'
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>Verify New Email</label>
      <p className={styles.fieldHint}>A code has been sent to <strong>{newEmail}</strong>.</p>
      <div style={{ margin: '0.5rem 0' }}>
        <OtpInput value={otp} onChange={setOtp} disabled={loading} />
      </div>
      {error && <div className={styles.errorBanner} style={{ marginBottom: '0.5rem' }}>{error}</div>}
      <div className={styles.detailsActions}>
        <button type="button" className={styles.saveBtn} disabled={loading} onClick={handleVerify}>
          {loading ? <span className={styles.spinnerSm} /> : null}
          Verify
        </button>
        <button type="button" className={styles.actionBtn} onClick={cancelEdit}>Cancel</button>
      </div>
      <p className={styles.fieldHint} style={{ marginTop: '0.4rem' }}>
        {resendCooldown > 0
          ? `Resend code in ${resendCooldown}s`
          : <button type="button" className={styles.deleteLink} style={{ color: '#1A1A1A' }} onClick={handleResend}>Resend code</button>
        }
      </p>
    </div>
  );
}

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({
    full_name: '', phone: '', email: '', date_of_birth: '',
    lane_address: '', pincode: '', district: '', state: '', username: '',
  });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk]     = useState(false);

  const [credits, setCredits]   = useState(null);

  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeError, setPincodeError]     = useState('');

  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving]   = useState(false);
  const [pwError, setPwError]     = useState('');
  const [pwOk, setPwOk]           = useState(false);

  useEffect(() => {
    const load = async () => {
      const profile = await apiGetProfile();
      if (profile) {
        setForm({
          full_name:     profile.full_name || '',
          phone:         profile.phone || '',
          email:         profile.email || '',
          date_of_birth: profile.date_of_birth ? profile.date_of_birth.slice(0, 10) : '',
          lane_address:  profile.lane_address || '',
          pincode:       profile.pincode || '',
          district:      profile.district || '',
          state:         profile.state || '',
          username:      profile.username || '',
        });
      }
      const c = await apiGetCredits().catch(() => null);
      setCredits(c);
      setLoading(false);
    };
    load();
  }, []);

  const handleChange = (e) => {
    setSaveOk(false);
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleEmailChanged = (newEmail) => {
    setForm(prev => ({ ...prev, email: newEmail }));
  };

  const handlePincodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPincodeError('');
    setForm(prev => ({ ...prev, pincode: val }));
  };

  const handlePincodeBlur = useCallback(async () => {
    if (form.pincode.length !== 6) return;
    setPincodeLoading(true);
    setPincodeError('');
    try {
      const result = await apiLookupPincode(form.pincode);
      if (result?.district && result?.state) {
        setForm(prev => ({ ...prev, district: result.district, state: result.state }));
      } else {
        setPincodeError('Could not auto-detect — please fill district/state manually.');
      }
    } catch {
      setPincodeError('Could not auto-detect — please fill district/state manually.');
    } finally {
      setPincodeLoading(false);
    }
  }, [form.pincode]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSaveOk(false);
    // email intentionally excluded — it goes through EmailChangeField's own
    // OTP flow, never through this general save (backend silently ignores
    // it anyway, but keeping it out of the payload here for clarity).
    const { email, ...saveable } = form;
    try {
      await apiUpdateProfile(saveable);
      setSaveOk(true);
      const returnTo = location.state?.returnTo;
      if (returnTo) {
        setTimeout(() => navigate(returnTo), 600);
      }
    } catch (err) {
      setSaveError(err.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    setPwError('');
    setPwOk(false);
    if (newPassword.length < 8) {
      setPwError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('Passwords do not match.');
      return;
    }
    setPwSaving(true);
    try {
      await apiChangePassword(newPassword, confirmPassword);
      setPwOk(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwError(err.message || 'Could not update password.');
    } finally {
      setPwSaving(false);
    }
  };

  const isPlus = credits?.total_purchased > 0;
  const initial = (user?.full_name || user?.username || '?').trim().charAt(0).toUpperCase();

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingWrap}>
          <span className={styles.spinnerDark} />
          Loading profile...
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <button className={styles.logoBtn} onClick={() => navigate('/')} type="button">
          <span className={styles.logoBtnIcon}>D</span>
          <span className={styles.logoBtnText}>Delefy <span className={styles.logoBtnVersion}>v1.0</span></span>
        </button>
        <button className={styles.navBtn} onClick={() => navigate('/')} type="button">
          Back to Dashboard
        </button>
      </header>

      <main className={styles.main}>

        {/* ── Subscription Status Card ── */}
        <div className={styles.statusCard}>
          <div className={styles.statusTop}>
            <div className={styles.statusIdentity}>
              <span className={styles.avatar}>{initial}</span>
              <div className={styles.statusNames}>
                <span className={styles.statusFullName}>{user?.full_name || form.full_name || 'Your Account'}</span>
                <span className={styles.statusUsername}>@{form.username || user?.username}</span>
              </div>
            </div>
            <span className={`${styles.planPill} ${isPlus ? styles.planPillPlus : styles.planPillFree}`}>
              {isPlus ? 'Plus Bundle' : 'Free Tier'}
            </span>
          </div>

          <div className={styles.statusStrip}>
            <div className={styles.statCell}>
              <span className={styles.statLabel}>Plan</span>
              <span className={styles.statValue}>{isPlus ? 'Plus Bundle' : 'Free Tier'}</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statCell}>
              <span className={styles.statLabel}>Bill Credits Left</span>
              <span className={styles.statValueLarge}>{credits?.credits_available ?? 0}</span>
            </div>
          </div>
        </div>

        {/* ── Action Row ── */}
        <div className={styles.actionRow}>
          <button className={styles.actionBtnPrimary} onClick={() => navigate('/payment')} type="button">
            Purchase &amp; History
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => alert('Send us your queries, suggestions, feedback & complaints on support.delefy@rijainone.com')}
            type="button"
          >
            Contact Us
          </button>
          <button className={styles.actionBtn} onClick={() => navigate('/legal')} type="button">
            Terms &amp; Privacy
          </button>
          <button className={styles.actionBtnPrimary} onClick={logout} type="button">
            Log Out
          </button>
        </div>

        {/* ── Profile Details ── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Profile Details</h2>
            <p className={styles.sectionDesc}>Keep your details up to date. This is what appears on your invoices.</p>
          </div>

          <div className={styles.detailsCard}>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Full Name</label>
                <input className={styles.input} name="full_name" value={form.full_name} onChange={handleChange} />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Phone Number</label>
                <input className={styles.input} name="phone" value={form.phone} onChange={handleChange} />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <EmailChangeField currentEmail={form.email} onChanged={handleEmailChanged} />
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Date of Birth</label>
                <input className={styles.input} type="date" name="date_of_birth" value={form.date_of_birth} onChange={handleChange} />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Address (House No. &amp; Lane)</label>
              <input className={styles.input} name="lane_address" value={form.lane_address} onChange={handleChange} />
            </div>

            <div className={styles.fieldRow3}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Pincode</label>
                <input
                  className={styles.input}
                  name="pincode"
                  value={form.pincode}
                  onChange={handlePincodeChange}
                  onBlur={handlePincodeBlur}
                  inputMode="numeric"
                  maxLength={6}
                />
                {pincodeLoading && <span className={styles.fieldHint}>Detecting district &amp; state...</span>}
                {pincodeError && <span className={styles.fieldHintWarn}>{pincodeError}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>District</label>
                <input className={styles.input} name="district" value={form.district} onChange={handleChange} />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>State</label>
                <input className={styles.input} name="state" value={form.state} onChange={handleChange} />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Username</label>
              <input className={styles.input} name="username" value={form.username} onChange={handleChange} maxLength={20} />
              <span className={styles.fieldHint}>3–20 characters. Must be unique.</span>
            </div>

            {saveError && <div className={styles.errorBanner}>{saveError}</div>}
            {saveOk && <div className={styles.okBanner}>Saved successfully.</div>}

            <div className={styles.detailsActions}>
              <button className={styles.saveBtn} onClick={handleSave} disabled={saving} type="button">
                {saving ? <span className={styles.spinnerSm} /> : null}
                {saving ? 'Saving...' : 'Save Details'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Change Password ── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Change Password</h2>
            <p className={styles.sectionDesc}>You're signed in, so we don't need your current password. Just set a new one.</p>
          </div>

          <div className={styles.detailsCard}>
            <div className={styles.fieldRow}>
              <PasswordField
                label="New Password"
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setPwOk(false); }}
                placeholder="At least 8 characters"
              />
              <PasswordField
                label="Confirm New Password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setPwOk(false); }}
                placeholder="Re-enter new password"
              />
            </div>

            {pwError && <div className={styles.errorBanner}>{pwError}</div>}
            {pwOk && <div className={styles.okBanner}>Password updated successfully.</div>}

            <div className={styles.detailsActions}>
              <button className={styles.saveBtn} onClick={handlePasswordSave} disabled={pwSaving} type="button">
                {pwSaving ? <span className={styles.spinnerSm} /> : null}
                {pwSaving ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Delete Account ── */}
        <div className={styles.deleteRow}>
          <span>
            To delete this account permanently,{' '}
            <button className={styles.deleteLink} onClick={() => navigate('/profile/delete')} type="button">
              click here
            </button>.
          </span>
        </div>

      </main>
    </div>
  );
}